import { RIBBON_ID } from '../ribbon-base/ribbon-constants';
import { createElement, isNullOrUndefined } from '@syncfusion/ej2-base';
import { RibbonItemSize } from '@syncfusion/ej2-ribbon';
import { BulletListHelper } from '../../helper/bullet-list-helper';
// Bullet list constants
export const BULLET_LIST_ID = '_bullet_list';
/**
 * BulletsGroup class for handling bullet list operations in Document Editor ribbon
 * @private
 */
export class BulletsGroup {
    /**
     * Constructor for BulletsGroup
     * @param {DocumentEditorContainer} container - DocumentEditorContainer instance
     */
    constructor(container) {
        // Track the last applied bullet style
        this.appliedBulletStyle = 'dot';
        this.eventHandlers = {};
        // HTML Elements for bullet options
        this.bulletElements = {};
        this.container = container;
        this.ribbonId = this.container.element.id + RIBBON_ID;
        this.localObj = this.container.localObj;
    }
    get documentEditor() {
        return this.container.documentEditor;
    }
    /**
     * Get the bullet list split button item configuration
     * @returns {RibbonItemModel} The ribbon item model for bullet list
     * @private
     */
    getBulletListItem() {
        const id = this.ribbonId;
        return {
            type: 'SplitButton',
            allowedSizes: RibbonItemSize.Small,
            keyTip: 'U',
            splitButtonSettings: this.createBulletSplitButton(),
            id: id + BULLET_LIST_ID,
            ribbonTooltipSettings: { content: this.localObj.getConstant('Bullets') }
        };
    }
    /**
     * Create bullet dropdown in ribbon
     * @returns {RibbonSplitButtonSettingsModel} SplitButtonModel configuration
     * @private
     */
    createBulletSplitButton() {
        let bulletIconCss = 'e-de-ctnr-bullets e-icons';
        if (this.container.enableRtl) {
            bulletIconCss += ' e-de-flip';
        }
        // Create the HTML template for bullets dropdown
        const bulletDropDiv = createElement('div', {
            id: this.ribbonId + '_bullet_list_div',
            styles: 'width: 196px;height: auto;visibility: hidden'
        });
        const bulletDropUlTag = createElement('ul', {
            styles: 'visibility: visible; outline: 0px;',
            id: this.ribbonId + '_listMenu',
            className: 'e-de-floating-menu e-de-bullets-menu e-de-list-container e-de-list-thumbnail'
        });
        bulletDropDiv.appendChild(bulletDropUlTag);
        // Create bullet list options using a more efficient approach
        this.createBulletElements(bulletDropUlTag);
        return {
            target: bulletDropDiv,
            iconCss: bulletIconCss,
            content: this.localObj.getConstant('Bullets'),
            items: this.getBulletItems(),
            select: this.handleBulletSelection.bind(this),
            beforeOpen: () => {
                bulletDropDiv.style.visibility = 'visible';
                if (!isNullOrUndefined(this.documentEditor.selectionModule)) {
                    if (isNullOrUndefined(this.documentEditor.selectionModule.paragraphFormat.listId) ||
                        this.documentEditor.selectionModule.paragraphFormat.listId === -1) {
                        // No current list format
                        this.updateSelectedBulletListType(this.documentEditor.selectionModule.paragraphFormat.listText);
                    }
                    else {
                        // Get current bullet list format
                        const startParagraph = this.documentEditor.selectionModule.isForward ?
                            this.documentEditor.selectionModule.start.paragraph : this.documentEditor.selectionModule.end.paragraph;
                        this.updateSelectedBulletListType(startParagraph.paragraphFormat.listFormat.listLevel.numberFormat);
                    }
                }
            },
            beforeClose: () => {
                bulletDropDiv.style.visibility = 'hidden';
                this.removeSelectedList();
            },
            click: () => {
                this.applyLastAppliedBullet();
            },
            close: () => {
                this.refreshHomeSelection();
            }
        };
    }
    createBulletElements(ulTag) {
        // Define bullet types with their properties
        const bulletTypes = [
            { key: 'none', cssClass: 'e-de-ctnr-bullet-none e-icons e-de-ctnr-list', isNone: true, handler: this.bulletNoneClick.bind(this) },
            { key: 'dot', cssClass: 'e-de-ctnr-bullet-dot e-icons e-de-ctnr-list', isNone: false, handler: this.bulletDotClick.bind(this) },
            { key: 'circle', cssClass: 'e-de-ctnr-bullet-circle e-icons e-de-ctnr-list', isNone: false, handler: this.bulletCircleClick.bind(this) },
            { key: 'square', cssClass: 'e-de-ctnr-bullet-square e-icons e-de-ctnr-list', isNone: false, handler: this.bulletSquareClick.bind(this) },
            { key: 'flower', cssClass: 'e-de-ctnr-bullet-flower e-icons e-de-ctnr-list', isNone: false, handler: this.bulletFlowerClick.bind(this) },
            { key: 'arrow', cssClass: 'e-de-ctnr-bullet-arrow e-icons e-de-ctnr-list', isNone: false, handler: this.bulletArrowClick.bind(this) },
            { key: 'tick', cssClass: 'e-de-ctnr-bullet-tick e-icons e-de-ctnr-list', isNone: false, handler: this.bulletTickClick.bind(this) }
        ];
        // Create all elements and store them
        bulletTypes.forEach((type) => {
            const element = this.createBulletListTag(ulTag, type.cssClass, type.isNone);
            element.addEventListener('click', type.handler);
            this.bulletElements[type.key] = element;
            this.eventHandlers[type.key] = { element, handler: type.handler };
        });
    }
    getBulletItems() {
        return [
            { id: this.ribbonId + '_bullet-none', text: this.localObj.getConstant('None') },
            { id: this.ribbonId + '_bullet-dot', text: this.localObj.getConstant('Dot') },
            { id: this.ribbonId + '_bullet-circle', text: this.localObj.getConstant('Circle') },
            { id: this.ribbonId + '_bullet-square', text: this.localObj.getConstant('Square') },
            { id: this.ribbonId + '_bullet-flower', text: this.localObj.getConstant('Flower') },
            { id: this.ribbonId + '_bullet-arrow', text: this.localObj.getConstant('Arrow') },
            { id: this.ribbonId + '_bullet-tick', text: this.localObj.getConstant('Tick') }
        ];
    }
    createBulletListTag(ulTag, iconCss, isNone) {
        return BulletListHelper.createBulletListTag(ulTag, iconCss, isNone, this.localObj);
    }
    updateSelectedBulletListType(listText) {
        BulletListHelper.updateSelectedBulletListType(listText, this.bulletElements);
    }
    removeSelectedList() {
        BulletListHelper.removeSelectedList(this.bulletElements);
    }
    /**
     * Apply the last used bullet style
     * @returns {void}
     * @private
     */
    applyLastAppliedBullet() {
        this.applyBulletStyle(this.appliedBulletStyle);
    }
    /**
     * Handle bullet style selection from dropdown
     * @param {any} args - The event arguments
     * @returns {void}
     * @private
     */
    handleBulletSelection(args) {
        const styleMap = {};
        styleMap[this.localObj.getConstant('None')] = 'none';
        styleMap[this.localObj.getConstant('Dot')] = 'dot';
        styleMap[this.localObj.getConstant('Circle')] = 'circle';
        styleMap[this.localObj.getConstant('Square')] = 'square';
        styleMap[this.localObj.getConstant('Flower')] = 'flower';
        styleMap[this.localObj.getConstant('Arrow')] = 'arrow';
        styleMap[this.localObj.getConstant('Tick')] = 'tick';
        const style = styleMap[args.item.text];
        if (style) {
            this.applyBulletStyle(style);
        }
    }
    applyBulletStyle(style) {
        const appliedStyle = { value: this.appliedBulletStyle };
        BulletListHelper.applyBulletStyle(this.documentEditor, style, appliedStyle);
        this.appliedBulletStyle = appliedStyle.value;
        this.refreshHomeSelection();
    }
    bulletNoneClick() {
        BulletListHelper.clearList(this.documentEditor);
        this.refreshHomeSelection();
    }
    bulletDotClick() {
        this.applyBulletStyle('dot');
    }
    bulletCircleClick() {
        this.applyBulletStyle('circle');
    }
    bulletSquareClick() {
        this.applyBulletStyle('square');
    }
    bulletFlowerClick() {
        this.applyBulletStyle('flower');
    }
    bulletArrowClick() {
        this.applyBulletStyle('arrow');
    }
    bulletTickClick() {
        this.applyBulletStyle('tick');
    }
    destroy() {
        // Remove event listeners
        const keys = Object.keys(this.eventHandlers);
        for (const key of keys) {
            /* eslint-disable */
            const item = this.eventHandlers[key];
            if (item.element && item.handler) {
                item.element.removeEventListener('click', item.handler);
            }
        }
        // Clear references
        this.eventHandlers = {};
        this.bulletElements = {};
    }
    refreshHomeSelection() {
        const ribbonModule = this.container.ribbonModule;
        if (ribbonModule && ribbonModule.tabManager && ribbonModule.tabManager.homeTab) {
            ribbonModule.tabManager.homeTab.updateSelection();
        }
    }
}
